// Split out of CompactPhaseScores.tsx (react-refresh/only-export-components)
// — plain data-transform helpers, not components. Behavior unchanged.

/**
 * Normalize score to 0-100 range
 * Handles both 0-1 and 0-100 input ranges
 */
export function normalizeScore(score: number | undefined): number {
  if (score === undefined) return 0;
  return score > 1 ? score : score * 100;
}

/**
 * Get score badge variant based on percentage
 */
export function getScoreVariant(score: number): 'success' | 'warning' | 'error' {
  if (score >= 80) return 'success';
  if (score >= 60) return 'warning';
  return 'error';
}

/**
 * Returns display properties for a trajectory_factor value.
 * factor >= 0.9 → good (no indicator shown inline, just a subtle dot)
 * factor 0.7–0.9 → warning (amber)
 * factor < 0.7 → poor (red)
 * null → data unavailable (grey)
 */
export function getTrajectoryDisplay(factor: number | null): {
  color: string;
  dotColor: string;
  label: string;
  tooltip: string;
} {
  const base =
    'Trajectory quality measures how similar the movement PATH is, not just the final position. ' +
    'A low score means the body part reached a similar pose but through a different movement.';

  if (factor === null) {
    return {
      color: 'text-text-muted',
      dotColor: 'bg-text-muted/40',
      label: 'N/A',
      tooltip: `Trajectory data unavailable (static phase or motion quality not computed). ${base}`,
    };
  }
  if (factor >= 0.9) {
    return {
      color: 'text-success',
      dotColor: 'bg-success',
      label: `${Math.round(factor * 100)}%`,
      tooltip: `Path match: ${Math.round(factor * 100)}% — movement path closely matches the template. ${base}`,
    };
  }
  if (factor >= 0.7) {
    return {
      color: 'text-warning',
      dotColor: 'bg-warning',
      label: `${Math.round(factor * 100)}%`,
      tooltip: `Path match: ${Math.round(factor * 100)}% — movement path differs slightly from template. ${base}`,
    };
  }
  return {
    color: 'text-danger',
    dotColor: 'bg-danger',
    label: `${Math.round(factor * 100)}%`,
    tooltip: `Path match: ${Math.round(factor * 100)}% — movement path significantly different from template. ${base}`,
  };
}
